import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../services/api';
import { User } from '../types';

const Profile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    username: ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.getProfile();
      console.log('Profile response:', response);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        setProfileForm({
          name: response.data.user.name,
          username: response.data.user.username
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setSnackbar({ open: true, message: 'Failed to fetch profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setUpdating(true);
      const response = await api.updateProfile(profileForm);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        setEditing(false);
        setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      const message = error.response?.data?.message || 'Failed to update profile';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSnackbar({ open: true, message: 'New passwords do not match', severity: 'error' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setSnackbar({ open: true, message: 'New password must be at least 6 characters', severity: 'error' });
      return;
    }

    try {
      setUpdating(true);
      const response = await api.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      if (response.success) {
        setPasswordDialog(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      const message = error.response?.data?.message || 'Failed to change password';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setProfileForm({
      name: user?.name || '',
      username: user?.username || ''
    });
  };

  const handleLogout = () => {
    api.logout();
    window.location.href = '/login';
  };

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'File size must be less than 5MB', severity: 'error' });
      return;
    }

    try {
      setUpdating(true);
      const response = await api.uploadProfilePicture(file);
      if (response.success && response.data?.profile_picture) {
        setUser(prev => prev ? { ...prev, profile_picture: response.data!.profile_picture } : null);
        setSnackbar({ open: true, message: 'Profile picture uploaded successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      const message = error.response?.data?.message || 'Failed to upload profile picture';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      setUpdating(true);
      const response = await api.deleteProfilePicture();
      if (response.success) {
        setUser(prev => prev ? { ...prev, profile_picture: undefined } : null);
        setSnackbar({ open: true, message: 'Profile picture deleted successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Error deleting profile picture:', error);
      const message = error.response?.data?.message || 'Failed to delete profile picture';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const getProfilePictureUrl = (profilePicture?: string) => {
    if (!profilePicture) return undefined;
    // Remove /api from the base URL since static files are served directly
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5001').replace('/api', '');
    const url = `${baseUrl}${profilePicture}`;
    console.log('Profile picture URL:', url);
    return url;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Profile Settings
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Profile Information Card */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Profile Information
                </Typography>
                {!editing && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => setEditing(true)}
                    variant="outlined"
                    size="small"
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>

              {editing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Full Name"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      required
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <TextField
                      label="Username"
                      value={profileForm.username}
                      onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                      required
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      startIcon={<SaveIcon />}
                      onClick={handleProfileUpdate}
                      variant="contained"
                      disabled={updating}
                    >
                      {updating ? <CircularProgress size={20} /> : 'Save Changes'}
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                      variant="outlined"
                      disabled={updating}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Full Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user?.name}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmailIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Username
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user?.username}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LockIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Password
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setPasswordDialog(true)}
                      >
                        Change Password
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* User Avatar and Quick Actions */}
        <Box sx={{ width: 300 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={getProfilePictureUrl(user?.profile_picture)}
                  imgProps={{ crossOrigin: "anonymous" }}
                  sx={{
                    width: 100,
                    height: 100,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onError={(e) => {
                    console.error('Failed to load profile picture:', e);
                    console.log('Attempted URL:', getProfilePictureUrl(user?.profile_picture));
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }}
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={updating}
                >
                  <PhotoCameraIcon />
                </IconButton>
                {user?.profile_picture && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'error.dark',
                      }
                    }}
                    size="small"
                    onClick={handleDeleteProfilePicture}
                    disabled={updating}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleProfilePictureUpload}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Click avatar to upload photo
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                {user?.name}
              </Typography>
              <Chip
                label={user?.username}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
              <Divider sx={{ my: 2 }} />
              <Button
                variant="outlined"
                color="error"
                onClick={handleLogout}
                fullWidth
              >
                Logout
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              required
              helperText="Password must be at least 6 characters"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={updating || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
          >
            {updating ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile; 